import {
	ClientEntityType,
	JobDocumentEntityType,
	JobEntityType,
	JobItemEntityType,
	OrganizationEntityType,
	PatientEntityType,
	PractitionerEntityType,
} from "@/lib/supabase/supabaseTypes";
import { PdfTemplateType } from "../../pdf-templates/document-template.types";
import { KostenvoranschlagTemplate } from "../../pdf-templates/pdf-templates/kostenvoranschlag.template";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import {
	CalculateTotalsResult,
	calculateSammelrechnungRowsAndTotals,
} from "../../lib/utils/calculate";
import { LieferscheinTemplate } from "../../pdf-templates/pdf-templates/lieferschein.template";
import { GutschriftTemplate } from "../../pdf-templates/pdf-templates/gutschrift.template";
import { RechnungTemplate } from "../../pdf-templates/pdf-templates/rechnung.template";
import { PaymentSlipDataInput } from "../../pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { SammelrechnungTemplate } from "../../pdf-templates/pdf-templates/sammelrechnung.template";
import { KartenzahlungsquittungTemplate } from "../../pdf-templates/pdf-templates/kartenzahlungsquittung.template";
import { BarzahlungsquittungTemplate } from "../../pdf-templates/pdf-templates/barzahlungsquittung.template";
import { AkontorechnungTemplate } from "../../pdf-templates/pdf-templates/akontorechnung.template";
import { PaymentReminderTemplate } from "../../pdf-templates/pdf-templates/payment-reminder.template";
import { getPaymentReminderDocumentExample } from "../../constants/paymentReminderDocumentExample";
import { ReminderTemplateType } from "../debitoren/mahnwesen/utils/reminder-stage";
import { AuftragsblattTemplate } from "../../pdf-templates/pdf-templates/auftragsblatt.template";
import { JobOrderLabelTemplate } from "../../pdf-templates/pdf-templates/job-order-label.template";
import { CommonPDFTemplateProps } from "../../pdf-templates/pdf-templates/types";
import { useRegionKind } from "../../lib/utils/region-utils";
import { getDeliveryNoteTitle } from "../job-page/job-document/job-document.types";

export const PdfTemplateOptions = ({
	client,
	jobExample,
	jobItems,
	jobDocumentExample,
	organization,
	pdfTemplateConfiguration,
	patientExample,
	practitionerExample,
	jobTotals,
	paymentSlipData,
	sammelrechnungCalculationResult,
	selectedPdfTemplate,
	clientAvatarUrl,
	currencyConverterValues,
}: {
	client: ClientEntityType;
	jobExample: JobEntityType;
	jobItems: JobItemEntityType[];
	jobDocumentExample: JobDocumentEntityType;
	organization: OrganizationEntityType;
	pdfTemplateConfiguration: PdfTemplateConfiguration | undefined;
	patientExample: PatientEntityType;
	practitionerExample: PractitionerEntityType;
	jobTotals: CalculateTotalsResult;
	paymentSlipData: PaymentSlipDataInput;
	sammelrechnungCalculationResult: ReturnType<
		typeof calculateSammelrechnungRowsAndTotals
	>;
	selectedPdfTemplate: PdfTemplateType;
	clientAvatarUrl?: string;
	currencyConverterValues?: Record<string, string>;
}) => {
	const region = useRegionKind(client);
	const commonProps: CommonPDFTemplateProps = {
		organization,
		pdfTemplateConfiguration,
		client,
		job: jobExample,
		jobItems: jobItems.slice(0, 10),
		jobDocument: jobDocumentExample,
		jobTotals,
		currencyConverterValues,
	};
	const reminderPreview = (templateType: ReminderTemplateType) => (
		<PaymentReminderTemplate
			document={getPaymentReminderDocumentExample(templateType, client)}
			organization={organization}
			pdfTemplateConfiguration={pdfTemplateConfiguration}
			bankAccount={paymentSlipData.bankAccount}
			currencyConverterValues={currencyConverterValues}
		/>
	);
	const templates = {
		[PdfTemplateType.KOSTENVORANSCHLAG]: (
			<KostenvoranschlagTemplate
				{...commonProps}
				patient={patientExample}
				practitioner={practitionerExample}
			/>
		),
		[PdfTemplateType.LIEFERSCHEIN]: (
			<LieferscheinTemplate
				{...commonProps}
				jobDocument={{
					...jobDocumentExample,
					title: getDeliveryNoteTitle(region),
				}}
				patient={patientExample}
				practitioner={practitionerExample}
			/>
		),
		[PdfTemplateType.GUTSCHRIFT]: <GutschriftTemplate {...commonProps} />,
		[PdfTemplateType.RECHNUNG]: (
			<RechnungTemplate
				{...commonProps}
				patient={patientExample}
				practitioner={practitionerExample}
				paymentSlipData={paymentSlipData}
			/>
		),
		[PdfTemplateType.SAMMELRECHNUNG]: (
			<SammelrechnungTemplate
				recipient={client}
				client={client}
				organization={organization}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
				sammelrechnungCalculationResult={
					sammelrechnungCalculationResult
				}
				paymentSlipData={paymentSlipData}
				currentDate={new Date().toISOString()}
				currencyConverterValues={currencyConverterValues}
			/>
		),

		[PdfTemplateType.KARTENZAHLUNGSQUITTUNG]: (
			<KartenzahlungsquittungTemplate
				{...commonProps}
				patient={patientExample}
				practitioner={practitionerExample}
				paymentSlipData={paymentSlipData}
			/>
		),
		[PdfTemplateType.BARZAHLUNGSQUITTUNG]: (
			<BarzahlungsquittungTemplate
				{...commonProps}
				patient={patientExample}
				practitioner={practitionerExample}
				paymentSlipData={paymentSlipData}
			/>
		),
		[PdfTemplateType.AKONTORECHNUNG]: (
			<AkontorechnungTemplate
				{...commonProps}
				jobItems={jobItems}
				paymentSlipData={paymentSlipData}
				date="01.01.2022" //TODO(pdf)
				patient={patientExample}
			/>
		),

		[PdfTemplateType.ZAHLUNGSERINNERUNG]: reminderPreview(
			PdfTemplateType.ZAHLUNGSERINNERUNG
		),
		[PdfTemplateType.ERSTE_MAHNUNG]: reminderPreview(
			PdfTemplateType.ERSTE_MAHNUNG
		),
		[PdfTemplateType.FOLGEMAHNUNG]: reminderPreview(
			PdfTemplateType.FOLGEMAHNUNG
		),
		[PdfTemplateType.LETZTE_MAHNUNG]: reminderPreview(
			PdfTemplateType.LETZTE_MAHNUNG
		),
		[PdfTemplateType.AUFTRAGSBLATT]: (
			<AuftragsblattTemplate
				{...commonProps}
				jobItems={jobItems}
				patient={patientExample}
				qrCodeUrl="https://dentdesk.ch"
				clientAvatarUrl={clientAvatarUrl}
			/>
		),
		[PdfTemplateType.ETIK_AUFTRAG]: (
			<JobOrderLabelTemplate
				client={client}
				job={jobExample}
				organization={organization}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
				patient={patientExample}
				qrCodeUrl="https://dentdesk.ch"
			/>
		),
		[PdfTemplateType.RECHNUNGSDOPPEL]: <></>,
	};

	return templates[selectedPdfTemplate];
};
